// src/utils/jobService.js
import { 
  collection, 
  doc, 
  addDoc, 
  updateDoc, 
  setDoc,
  getDoc, 
  getDocs, 
  query, 
  increment,
  where, 
  orderBy, 
  limit,
  arrayUnion,
  arrayRemove,
  serverTimestamp,
  onSnapshot
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { db, storage } from '../firebase/config';

// Helper function to safely convert Firestore timestamps (add at top of file)
  const convertTimestamp = (timestamp) => {
    if (!timestamp) return null;
    
    // If it's already a string, return it
    if (typeof timestamp === 'string') return timestamp;
    
    // If it's a Firestore Timestamp, convert it
    if (timestamp && typeof timestamp.toDate === 'function') {
      return timestamp.toDate().toISOString();
    }
    
    // If it's a Date object, convert it
    if (timestamp instanceof Date) {
      return timestamp.toISOString();
    }
    
    return null;
  };

export const jobService = {
  // Job posting and management
  async postJob(organizationId, jobData) {
    try {
      const jobRef = await addDoc(collection(db, 'parrot-jobs'), {
        ...jobData,
        organizationId,
        clientId: jobData.clientId,
        status: 'open',
        applications: [],
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: jobRef.id, success: true };
    } catch (error) {
      console.error('Error posting job:', error);
      throw new Error('Failed to post job');
    }
  },

  async getJobs(filters = {}) {
    try {
      let q = collection(db, 'parrot-jobs');
      const constraints = [];
      
      // Apply filters
      if (filters.organizationId) {
        constraints.push(where('organizationId', '==', filters.organizationId));
      }
      
      if (filters.status) {
        constraints.push(where('status', '==', filters.status));
      }
      
      if (filters.assignedVA) {
        constraints.push(where('assignedVA', '==', filters.assignedVA));
      }
      
      if (filters.skillsRequired) {
        constraints.push(where('skillsRequired', 'array-contains-any', filters.skillsRequired));
      }
      
      // Add ordering
      constraints.push(orderBy('createdAt', 'desc'));
      
      // Add limit if specified
      if (filters.limit) {
        constraints.push(limit(filters.limit));
      }
      
      // Build query with all constraints
      q = query(q, ...constraints);
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      });
    } catch (error) {
      console.error('Error fetching jobs:', error);
      throw new Error('Failed to fetch jobs');
    }
  },

  async getJobById(jobId) {
    try {
      const jobDoc = await getDoc(doc(db, 'parrot-jobs', jobId));
      if (jobDoc.exists()) {
        return {
          id: jobDoc.id,
          ...jobDoc.data(),
          createdAt: jobDoc.data().createdAt?.toDate()?.toISOString(),
          updatedAt: jobDoc.data().updatedAt?.toDate()?.toISOString()
        };
      }
      throw new Error('Job not found');
    } catch (error) {
      console.error('Error fetching job:', error);
      throw error;
    }
  },

  async updateJob(jobId, updates) {
    try {
      await updateDoc(doc(db, 'parrot-jobs', jobId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating job:', error);
      throw new Error('Failed to update job');
    }
  },

  // VA Profile Management
  async createVAProfile(userId, profileData) {
    try {
      const vaRef = await addDoc(collection(db, 'parrot-va-profiles'), {
        userId,
        ...profileData,
        isActive: true,
        rating: 0,
        totalEarnings: 0,
        completedJobs: 0,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { id: vaRef.id, success: true };
    } catch (error) {
      console.error('Error creating VA profile:', error);
      throw new Error('Failed to create VA profile');
    }
  },

  // Updated getVAProfile method
  async getVAProfile(userId) {
    try {
      const q = query(
        collection(db, 'parrot-va-profiles'),
        where('userId', '==', userId)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docSnapshot = snapshot.docs[0];
        const data = docSnapshot.data();
        
        return {
          id: docSnapshot.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching VA profile:', error);
      throw new Error('Failed to fetch VA profile');
    }
  },

  async updateVAProfile(userId, updates) {
    try {
      // First, find the document by userId field
      const q = query(
        collection(db, 'parrot-va-profiles'),
        where('userId', '==', userId)
      );
      
      const snapshot = await getDocs(q);
      
      if (snapshot.empty) {
        // Profile doesn't exist, create it
        return await this.createVAProfile(userId, updates);
      }
      
      // Profile exists, update it using the actual document ID
      const profileDoc = snapshot.docs[0];
      await updateDoc(doc(db, 'parrot-va-profiles', profileDoc.id), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      
      return { success: true, id: profileDoc.id };
    } catch (error) {
      console.error('Error updating VA profile:', error);
      throw new Error('Failed to update VA profile');
    }
  },

  // Update VA profile by document ID (when you already have the profile ID)
  async updateVAProfileById(profileId, updates) {
    try {
      await updateDoc(doc(db, 'parrot-va-profiles', profileId), {
        ...updates,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating VA profile by ID:', error);
      throw new Error('Failed to update VA profile');
    }
  },

  async searchVAs(filters = {}) {
    try {
      let q = collection(db, 'parrot-va-profiles');
      const constraints = [];
      
      // Only apply availability filter if it's specified and not 'all'
      if (filters.availability && filters.availability !== 'all') {
        constraints.push(where('availability', '==', filters.availability));
      }
      
      // Only apply skills filter if skills array is not empty
      // Note: array-contains-any requires a non-empty array
      if (filters.skills && filters.skills.length > 0) {
        constraints.push(where('skills', 'array-contains-any', filters.skills));
      }
      
      // Apply constraints if any exist
      if (constraints.length > 0) {
        q = query(q, ...constraints);
      }
      
      const snapshot = await getDocs(q);
      let vas = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      // Client-side filtering for search text
      if (filters.search && filters.search.trim()) {
        const searchLower = filters.search.toLowerCase().trim();
        vas = vas.filter(va => 
          va.name?.toLowerCase().includes(searchLower) ||
          va.bio?.toLowerCase().includes(searchLower) ||
          va.skills?.some(skill => skill.toLowerCase().includes(searchLower))
        );
      }

      // Client-side filtering for minimum rating
      if (filters.minRating && filters.minRating > 0) {
        vas = vas.filter(va => (va.rating || 0) >= filters.minRating);
      }

      // Client-side filtering for price range
      if (filters.priceRange && filters.priceRange !== 'all') {
        const priceFilter = filters.priceRange.split('-');
        if (priceFilter.length === 2) {
          const min = parseInt(priceFilter[0]);
          const max = priceFilter[1] === '+' ? Infinity : parseInt(priceFilter[1]);
          vas = vas.filter(va => {
            const rate = va.hourlyRate || 0;
            return rate >= min && rate <= max;
          });
        }
      }

      // Sort by rating (highest first) by default
      vas.sort((a, b) => (b.rating || 0) - (a.rating || 0));

      return vas;
    } catch (error) {
      console.error('Error searching VAs:', error);
      throw new Error('Failed to search VAs: ' + error.message);
    }
  },

  // Get VA by ID
  async getVAById(vaId) {
    try {
      const vaDoc = await getDoc(doc(db, 'parrot-va-profiles', vaId));
      if (vaDoc.exists()) {
        return {
          id: vaDoc.id,
          ...vaDoc.data()
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching VA:', error);
      throw new Error('Failed to fetch VA profile');
    }
  },

  // Get all available VAs (simplified version without filters)
  async getAvailableVAs() {
    try {
      const q = query(
        collection(db, 'parrot-va-profiles'),
        where('availability', '==', 'available')
      );
      
      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching available VAs:', error);
      return [];
    }
  },
  // Job Applications
  async applyToJob(jobId, vaUserId, applicationData) {
    try {
      // Get VA profile to store the profile ID
      const vaProfile = await this.getVAProfile(vaUserId);
      if (!vaProfile) {
        throw new Error('VA profile not found');
      }

      const application = {
        ...applicationData,
        vaUserId, // User auth ID
        vaProfileId: vaProfile.id, // VA profile document ID
        jobId,
        status: 'pending',
        viewedByClient: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      };

      const appRef = await addDoc(collection(db, 'parrot-job-applications'), application);
      
      // Update job with application reference
      await updateDoc(doc(db, 'parrot-jobs', jobId), {
        applications: arrayUnion(appRef.id),
        updatedAt: serverTimestamp()
      });

      return { id: appRef.id, success: true };
    } catch (error) {
      console.error('Error applying to job:', error);
      throw new Error(error.message || 'Failed to submit application');
    }
  },

  async acceptApplication(jobId, vaId) {
    try {
      await updateDoc(doc(db, 'parrot-jobs', jobId), {
        assignedVA: vaId,
        status: 'in_progress',
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error accepting application:', error);
      throw new Error('Failed to accept application');
    }
  },

  // Statistics and Analytics
  async getClientStats(organizationId) {
    try {
      const jobs = await this.getJobs({ organizationId });
      
      const stats = {
        activeJobs: jobs.filter(job => ['open', 'in_progress'].includes(job.status)).length,
        completedJobs: jobs.filter(job => job.status === 'completed').length,
        totalSpent: jobs.filter(job => job.status === 'completed')
          .reduce((sum, job) => sum + (job.budget || 0), 0),
        avgRating: 4.8 // This would be calculated from actual ratings
      };
      
      return stats;
    } catch (error) {
      console.error('Error fetching client stats:', error);
      throw new Error('Failed to fetch statistics');
    }
  },

  async getVAStats(vaId) {
    try {
      const jobs = await this.getJobs({ assignedVA: vaId });
      
      const stats = {
        activeJobs: jobs.filter(job => job.status === 'in_progress').length,
        completedJobs: jobs.filter(job => job.status === 'completed').length,
        totalEarnings: jobs.filter(job => job.status === 'completed')
          .reduce((sum, job) => sum + (job.budget || 0), 0),
        avgRating: 4.9 // This would be calculated from actual ratings
      };
      
      return stats;
    } catch (error) {
      console.error('Error fetching VA stats:', error);
      throw new Error('Failed to fetch VA statistics');
    }
  },

  async getRecentJobs(organizationId, limitCount = 5) {
    try {
      return await this.getJobs({ 
        organizationId, 
        limit: limitCount 
      });
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
      return [];
    }
  },

  // Skills Management
  async getSkills() {
    try {
      const snapshot = await getDocs(collection(db, 'parrot-skills'));
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    } catch (error) {
      console.error('Error fetching skills:', error);
      return [];
    }
  },

  // File uploads
  async uploadJobAttachment(jobId, file) {
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `jobs/${jobId}/attachments/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading attachment:', error);
      throw new Error('Failed to upload attachment');
    }
  },

  // Real-time subscriptions
  subscribeToJobs(organizationId, callback) {
    const q = query(
      collection(db, 'parrot-jobs'),
      where('organizationId', '==', organizationId),
      orderBy('updatedAt', 'desc')
    );
    
    return onSnapshot(q, (snapshot) => {
      const jobs = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()?.toISOString(),
        updatedAt: doc.data().updatedAt?.toDate()?.toISOString()
      }));
      callback(jobs);
    });
  },

  async getVAProfileById(profileId) {
    try {
      const profileDoc = await getDoc(doc(db, 'parrot-va-profiles', profileId));
      if (profileDoc.exists()) {
        const data = profileDoc.data();
        
        return {
          id: profileDoc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching VA profile by ID:', error);
      throw new Error('Failed to fetch VA profile');
    }
  },

  async uploadVAPhoto(userId, file) {
    try {
      const fileName = `${Date.now()}_${file.name}`;
      const storageRef = ref(storage, `va-profiles/${userId}/photos/${fileName}`);
      
      await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(storageRef);
      
      return downloadURL;
    } catch (error) {
      console.error('Error uploading VA photo:', error);
      throw new Error('Failed to upload photo');
    }
  },

  // Job History and Ratings
  async getVAJobHistory(vaProfileId) {
    try {
      // For now, return mock data with ratings and reviews
      return [
        {
          id: 'job1',
          title: 'Customer Database Management',
          description: 'Organized and updated customer database with 500+ entries',
          status: 'completed',
          budget: 2500,
          totalPaid: 2500,
          clientName: 'TechStart Solutions',
          createdAt: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString(),
          completedAt: new Date(Date.now() - 25 * 24 * 60 * 60 * 1000).toISOString(),
          rating: 5,
          review: 'Excellent work! Very thorough and completed ahead of schedule. Great communication throughout the project.',
          reviewDate: new Date(Date.now() - 24 * 24 * 60 * 60 * 1000).toISOString(),
          details: 'Cleaned and organized customer database, removed duplicates, standardized formats, and created backup system.',
          deliverables: [
            'Clean customer database with 500+ entries',
            'Duplicate removal report',
            'Data standardization documentation',
            'Backup and recovery system setup'
          ]
        },
        {
          id: 'job2',
          title: 'Social Media Content Creation',
          description: 'Created content calendar and posts for LinkedIn and Twitter',
          status: 'completed',
          budget: 1800,
          totalPaid: 1800,
          clientName: 'Digital Marketing Agency',
          createdAt: new Date(Date.now() - 45 * 24 * 60 * 60 * 1000).toISOString(),
          completedAt: new Date(Date.now() - 40 * 24 * 60 * 60 * 1000).toISOString(),
          rating: 4,
          review: 'Good quality content and met all deadlines. Would work with again.',
          reviewDate: new Date(Date.now() - 39 * 24 * 60 * 60 * 1000).toISOString(),
          details: 'Developed comprehensive social media strategy with content calendar for Q1.',
          deliverables: [
            '30-day content calendar',
            '15 LinkedIn posts',
            '20 Twitter posts',
            'Engagement strategy document'
          ]
        },
        {
          id: 'job3',
          title: 'Email Campaign Management',
          description: 'Set up and managed email marketing campaign for product launch',
          status: 'completed',
          budget: 3200,
          totalPaid: 3200,
          clientName: 'E-commerce Store',
          createdAt: new Date(Date.now() - 60 * 24 * 60 * 60 * 1000).toISOString(),
          completedAt: new Date(Date.now() - 55 * 24 * 60 * 60 * 1000).toISOString(),
          rating: 5,
          review: 'Outstanding results! Email campaign exceeded expectations with 25% open rate and 8% conversion rate.',
          reviewDate: new Date(Date.now() - 54 * 24 * 60 * 60 * 1000).toISOString(),
          vaResponse: 'Thank you for the great review! It was a pleasure working on your product launch campaign.',
          details: 'Designed and executed email marketing campaign for new product launch including segmentation and A/B testing.',
          deliverables: [
            'Email campaign setup',
            'Customer segmentation',
            'A/B testing results',
            'Performance analytics report'
          ]
        }
      ];
    } catch (error) {
      console.error('Error fetching VA job history:', error);
      return [];
    }
  },

  async submitJobRating(jobId, rating, review, clientId) {
    try {
      const ratingData = {
        jobId,
        rating,
        review,
        clientId,
        createdAt: serverTimestamp()
      };
      
      await addDoc(collection(db, 'parrot-job-ratings'), ratingData);
      
      // Update job with rating
      await updateDoc(doc(db, 'parrot-jobs', jobId), {
        rating,
        review,
        reviewDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error submitting job rating:', error);
      throw new Error('Failed to submit rating');
    }
  },

  async respondToReview(jobId, vaResponse, vaId) {
    try {
      await updateDoc(doc(db, 'parrot-jobs', jobId), {
        vaResponse,
        vaResponseDate: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      
      return { success: true };
    } catch (error) {
      console.error('Error responding to review:', error);
      throw new Error('Failed to respond to review');
    }
  },

  async getClientStats(organizationId) {
    try {
      const jobsSnapshot = await getDocs(
        query(
          collection(db, 'parrot-jobs'),
          where('organizationId', '==', organizationId)
        )
      );

      const jobs = jobsSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      
      const stats = {
        activeJobs: jobs.filter(j => j.status === 'open' || j.status === 'in_progress').length,
        completedJobs: jobs.filter(j => j.status === 'completed').length,
        totalSpent: jobs.reduce((sum, j) => sum + (j.budget || 0), 0),
        avgRating: 4.5 // Calculate from actual ratings when implemented
      };

      return stats;
    } catch (error) {
      console.error('Error fetching client stats:', error);
      return {
        activeJobs: 0,
        completedJobs: 0,
        totalSpent: 0,
        avgRating: 0
      };
    }
  },

  // Get recent jobs
  async getRecentJobs(organizationId, limitCount = 5) {
    try {
      const q = query(
        collection(db, 'parrot-jobs'),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        createdAt: doc.data().createdAt?.toDate()?.toISOString(),
        updatedAt: doc.data().updatedAt?.toDate()?.toISOString()
      }));
    } catch (error) {
      console.error('Error fetching recent jobs:', error);
      return [];
    }
  },

  async applyToJob(jobId, vaId, applicationData) {
    try {
      // Create application document
      const applicationRef = await addDoc(collection(db, 'parrot-job-applications'), {
        ...applicationData,
        jobId,
        vaId,
        status: 'pending',
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
        viewedByClient: false,
        clientResponse: null
      });

      // Update VA profile with applied job
      const vaProfileQuery = query(
        collection(db, 'parrot-va-profiles'),
        where('userId', '==', vaId)
      );
      const vaSnapshot = await getDocs(vaProfileQuery);
      
      if (!vaSnapshot.empty) {
        const vaProfileRef = vaSnapshot.docs[0].ref;
        const vaProfileData = vaSnapshot.docs[0].data();
        const appliedJobs = vaProfileData.appliedJobs || [];
        
        await updateDoc(vaProfileRef, {
          appliedJobs: [...appliedJobs, jobId],
          updatedAt: serverTimestamp()
        });
      }

      return { 
        success: true, 
        applicationId: applicationRef.id 
      };
    } catch (error) {
      console.error('Error applying to job:', error);
      throw new Error('Failed to submit application. Please try again.');
    }
  },

  // Get application by VA and Job
  async getApplicationByVAAndJob(vaId, jobId) {
    try {
      const q = query(
        collection(db, 'parrot-job-applications'),
        where('vaId', '==', vaId),
        where('jobId', '==', jobId)
      );
      
      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        const docData = snapshot.docs[0];
        const data = docData.data();
        return {
          id: docData.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      }
      return null;
    } catch (error) {
      console.error('Error fetching application:', error);
      return null;
    }
  },

  // Get all applications by VA
  async getApplicationsByVA(vaId, filters = {}) {
    try {
      let q = query(
        collection(db, 'parrot-job-applications'),
        where('vaId', '==', vaId),
        orderBy('createdAt', 'desc')
      );

      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      });
    } catch (error) {
      console.error('Error fetching VA applications:', error);
      throw new Error('Failed to fetch applications');
    }
  },

  // Get all applications for a job
  async getApplicationsByJob(jobId, filters = {}) {
    try {
      let q = query(
        collection(db, 'parrot-job-applications'),
        where('jobId', '==', jobId),
        orderBy('createdAt', 'desc')
      );

      if (filters.status) {
        q = query(q, where('status', '==', filters.status));
      }

      const snapshot = await getDocs(q);
      return snapshot.docs.map(doc => {
        const data = doc.data();
        return {
          id: doc.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      });
    } catch (error) {
      console.error('Error fetching job applications:', error);
      throw new Error('Failed to fetch applications');
    }
  },

  // Get single application by ID
  async getApplicationById(applicationId) {
    try {
      const docRef = doc(db, 'parrot-job-applications', applicationId);
      const docSnap = await getDoc(docRef);
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        return {
          id: docSnap.id,
          ...data,
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        };
      }
      throw new Error('Application not found');
    } catch (error) {
      console.error('Error fetching application:', error);
      throw error;
    }
  },

  // Update application status (for client/admin)
  async updateApplicationStatus(applicationId, status, additionalData = {}) {
    try {
      await updateDoc(doc(db, 'parrot-job-applications', applicationId), {
        status,
        ...additionalData,
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error updating application status:', error);
      throw new Error('Failed to update application');
    }
  },

  // Mark application as viewed by client
  async markApplicationViewed(applicationId) {
    try {
      await updateDoc(doc(db, 'parrot-job-applications', applicationId), {
        viewedByClient: true,
        viewedAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });
      return { success: true };
    } catch (error) {
      console.error('Error marking application as viewed:', error);
      throw new Error('Failed to update application');
    }
  },

  // Withdraw application (VA can withdraw)
  async withdrawApplication(applicationId, vaId) {
    try {
      // Verify ownership
      const app = await this.getApplicationById(applicationId);
      if (app.vaId !== vaId) {
        throw new Error('Unauthorized');
      }

      await updateDoc(doc(db, 'parrot-job-applications', applicationId), {
        status: 'withdrawn',
        withdrawnAt: serverTimestamp(),
        updatedAt: serverTimestamp()
      });

      return { success: true };
    } catch (error) {
      console.error('Error withdrawing application:', error);
      throw new Error('Failed to withdraw application');
    }
  },

  // Upload application files
  async uploadApplicationFile(vaId, jobId, file) {
    try {
      // Validate file size (10MB max)
      if (file.size > 10 * 1024 * 1024) {
        throw new Error('File size exceeds 10MB limit');
      }

      // Validate file type
      const allowedTypes = [
        'application/pdf',
        'application/msword',
        'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
        'image/jpeg',
        'image/jpg',
        'image/png',
        'image/gif'
      ];

      if (!allowedTypes.includes(file.type)) {
        throw new Error('Invalid file type. Allowed: PDF, DOC, DOCX, JPG, PNG, GIF');
      }

      const timestamp = Date.now();
      const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
      const fileName = `${timestamp}_${sanitizedFileName}`;
      const storageRef = ref(storage, `applications/${vaId}/${jobId}/${fileName}`);
      
      // Upload file
      const snapshot = await uploadBytes(storageRef, file);
      const downloadURL = await getDownloadURL(snapshot.ref);
      
      return {
        name: file.name,
        size: file.size,
        type: file.type,
        url: downloadURL,
        path: snapshot.ref.fullPath,
        uploadedAt: new Date().toISOString()
      };
    } catch (error) {
      console.error('Error uploading application file:', error);
      throw new Error(error.message || 'Failed to upload file');
    }
  },

  // Get application statistics for VA
  async getVAApplicationStats(vaId) {
    try {
      const applications = await this.getApplicationsByVA(vaId);
      
      const stats = {
        total: applications.length,
        pending: applications.filter(app => app.status === 'pending').length,
        reviewed: applications.filter(app => app.status === 'reviewed').length,
        shortlisted: applications.filter(app => app.status === 'shortlisted').length,
        accepted: applications.filter(app => app.status === 'accepted').length,
        rejected: applications.filter(app => app.status === 'rejected').length,
        withdrawn: applications.filter(app => app.status === 'withdrawn').length,
        responseRate: 0,
        avgResponseTime: null
      };

      // Calculate response rate
      const respondedApps = applications.filter(app => 
        ['reviewed', 'shortlisted', 'accepted', 'rejected'].includes(app.status)
      );
      if (applications.length > 0) {
        stats.responseRate = (respondedApps.length / applications.length) * 100;
      }

      return stats;
    } catch (error) {
      console.error('Error fetching VA application stats:', error);
      return {
        total: 0,
        pending: 0,
        reviewed: 0,
        shortlisted: 0,
        accepted: 0,
        rejected: 0,
        withdrawn: 0,
        responseRate: 0,
        avgResponseTime: null
      };
    }
  },

  // Get application statistics for a job (client view)
  async getJobApplicationStats(jobId) {
    try {
      const applications = await this.getApplicationsByJob(jobId);
      
      const stats = {
        total: applications.length,
        pending: applications.filter(app => app.status === 'pending').length,
        reviewed: applications.filter(app => app.status === 'reviewed').length,
        shortlisted: applications.filter(app => app.status === 'shortlisted').length,
        accepted: applications.filter(app => app.status === 'accepted').length,
        rejected: applications.filter(app => app.status === 'rejected').length,
        avgProposedRate: 0,
        rateRange: { min: 0, max: 0 }
      };

      // Calculate average proposed rate
      if (applications.length > 0) {
        const rates = applications.map(app => app.proposedRate).filter(rate => rate > 0);
        if (rates.length > 0) {
          stats.avgProposedRate = rates.reduce((sum, rate) => sum + rate, 0) / rates.length;
          stats.rateRange.min = Math.min(...rates);
          stats.rateRange.max = Math.max(...rates);
        }
      }

      return stats;
    } catch (error) {
      console.error('Error fetching job application stats:', error);
      return {
        total: 0,
        pending: 0,
        reviewed: 0,
        shortlisted: 0,
        accepted: 0,
        rejected: 0,
        avgProposedRate: 0,
        rateRange: { min: 0, max: 0 }
      };
    }
  },

  async getVAMyJobs(vaUserId, limitCount = 10) {
    try {
      const myJobs = [];

      // 1. Get pending applications (jobs applied for)
      const applicationsQuery = query(
        collection(db, 'parrot-job-applications'),
        where('vaId', '==', vaUserId),
        where('status', '==', 'pending'),
        orderBy('createdAt', 'desc'),
        limit(limitCount)
      );

      const applicationsSnapshot = await getDocs(applicationsQuery);
      
      // Fetch job details for each pending application
      for (const appDoc of applicationsSnapshot.docs) {
        const appData = appDoc.data();
        try {
          const jobDoc = await getDoc(doc(db, 'parrot-jobs', appData.jobId));
          if (jobDoc.exists()) {
            const jobData = jobDoc.data();
            myJobs.push({
              id: jobDoc.id,
              ...jobData,
              applicationId: appDoc.id,
              applicationStatus: appData.status,
              appliedAt: convertTimestamp(appData.createdAt),
              status: 'pending', // Override status to show it's pending application
              type: 'application',
              createdAt: convertTimestamp(jobData.createdAt),
              updatedAt: convertTimestamp(jobData.updatedAt)
            });
          }
        } catch (error) {
          console.error(`Error fetching job for application ${appDoc.id}:`, error);
        }
      }

      // 2. Get assigned/active jobs
      const assignedJobsQuery = query(
        collection(db, 'parrot-jobs'),
        where('assignedVA', '==', vaUserId),
        where('status', 'in', ['assigned', 'in_progress', 'active']),
        orderBy('updatedAt', 'desc'),
        limit(limitCount)
      );

      const assignedJobsSnapshot = await getDocs(assignedJobsQuery);
      
      assignedJobsSnapshot.docs.forEach(doc => {
        const data = doc.data();
        myJobs.push({
          id: doc.id,
          ...data,
          type: 'assigned',
          createdAt: convertTimestamp(data.createdAt),
          updatedAt: convertTimestamp(data.updatedAt)
        });
      });

      // Sort by most recent first (by updatedAt or appliedAt)
      myJobs.sort((a, b) => {
        const dateA = new Date(a.updatedAt || a.appliedAt || a.createdAt);
        const dateB = new Date(b.updatedAt || b.appliedAt || b.createdAt);
        return dateB - dateA;
      });

      return myJobs.slice(0, limitCount);
    } catch (error) {
      console.error('Error fetching VA my jobs:', error);
      throw new Error('Failed to fetch my jobs');
    }
  }
};